/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { analyticsTrack, analyticsTrack_search_multiple_results_then_click } from '../../../../analytics_utils';
import { IconsForDocumentFlags } from '../../../../documentVersionFlags/documentVersionFlags_utils';
import { selectSearchSourceType, selectPearlUserInfo, selectCanViewSearchPreview } from '../../../../Login/userSlice';
import { snackNotifications } from '../../../../notification/SnackBarUtils';
import { documentVersionStateToStr, numToNiceStr, removeFiltersFromAudience } from '../../../../utils';
import {
  fetchDocumentVersionsSearch,
  SearchDocumentVersionType,
  SearchMetadata,
  selectCreatorMode,
  setSearchQueryMetaData,
} from '../../../chatSlice';
import { useAudienceFilter } from '../../../hooks/ChatContext';
import { SearchCategoriesContext } from '../../../hooks/MessageContext';
import {
  AudienceType,
  DocVersionType,
  DocumentMetadataHighlightType,
  DocumentMetadataType,
  DocumentVersionSearchType,
  DocumentVersionType,
  MessageType,
} from '../../../../algorithm/types';
import { Badge } from '../../Badge';
import styles from './MultipleMatches.module.scss';
import { AppDispatch } from '../../../../../../app/store';
import { fixUpSummary, slugToUrl } from '../utils';
import Medication from './Medication/Medication';

const createSearchMetaData = (metadata: DocumentMetadataType, position: number, dispatch: AppDispatch) => {
  const searchMetadata = {
    index: 'doc',
    objectIDs: [metadata.objectID],
    queryID: metadata.queryID,
    positions: [position],
  } as SearchMetadata;
  dispatch(setSearchQueryMetaData(searchMetadata));
};
const getDocumentVersionTitle = (version: DocumentVersionType | DocumentVersionSearchType) => {
  if (version.metadata?.highlight?.title?.has_highlight && version.metadata?.highlight?.title?.value)
    return version.metadata.highlight.title.value;
  return version.title;
};

function DocumentVersionList({
  searchId /* random id just for analytics */,
  areTriages,
  showNumMatches,
  publishedOnly,
  sayAlso,
  audienceFilter,
  documentVersions,
  searchTerm,
  showingMoreResults,
  triagesLength,
  showAudienceLabels,
}: {
  areTriages: boolean;
  audienceFilter: AudienceType;
  documentVersions: SearchDocumentVersionType;
  searchTerm: string;
  showNumMatches?: boolean;
  showingMoreResults?: boolean;
  publishedOnly?: boolean;
  sayAlso: boolean;
  searchId: string;
  triagesLength?: number;
  showAudienceLabels: boolean;
}) {
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const dispatch = useAppDispatch();
  const searchSourceType = useAppSelector(selectSearchSourceType);
  const canViewSearchPreview = useAppSelector(selectCanViewSearchPreview);
  const isCreatorMode = useAppSelector(selectCreatorMode);
  const doVersionPreviewsExist = useMemo(() => {
    return documentVersions.some((x) => {
      const highlight = x.metadata?.highlight;
      return (
        highlight?.title?.has_highlight ||
        highlight?.content?.has_highlight ||
        highlight?.doc_categories?.has_highlight ||
        highlight?.tags?.has_highlight
      );
    });
  }, [documentVersions]);
  /* When we have multiple matches ,we also show the summary.
     Please see :https://pearl-project.atlassian.net/wiki/spaces/MedPearl/pages/479035447/Search+Preview
     The order of precedence for search results is specialty->title->triggers(tags)->pagecontent.
     The title, as a hyperlink would always be shown.We show the specialty or tags or summary.
   */
  return (
    <div>
      {!canViewSearchPreview && (
        <div className={styles.container}>
          We have {sayAlso ? 'also ' : undefined}found {showNumMatches ? numToNiceStr(documentVersions.length) : ''}{' '}
          {areTriages ? 'triage' : 'document'} match
          {documentVersions.length > 1 ? 'es' : ''}
          &nbsp;to the term <span className={styles.messageText}>{searchTerm}</span>
        </div>
      )}
      <ul
        className={styles.listContainer}
        data-can-view-preview={canViewSearchPreview && doVersionPreviewsExist}
        style={isCreatorMode ? undefined : { margin: 0 }}
      >
        {documentVersions.map((v, index) => {
          const linkAudience = removeFiltersFromAudience(v.audiences, audienceFilter);
          const documentVersionMetaDataHighlight = v.metadata?.highlight;
          return (
            <li
              key={index}
              className={showingMoreResults ? styles.moreresults : styles.documentVersionListItem}
              data-can-view-preview={canViewSearchPreview && doVersionPreviewsExist}
            >
              <div className={styles.multipleMatchesContainer} data-creatormode={isCreatorMode}>
                {isCreatorMode && (
                  <IconsForDocumentFlags flags={v.flags} fontSize="small" containerStyle={{ marginRight: 0 }} />
                )}
                <Link
                  to={slugToUrl(v.document?.slug, v.title, searchTerm, v.id, publishedOnly, linkAudience)}
                  onClick={(e) => {
                    // create the metadata to be sent to algolia event
                    // document position is overall position in the list inclusive of triages list, algolia position starts from 1 not 0.
                    createSearchMetaData(v.metadata!, (triagesLength ?? 0) + index + 1, dispatch);

                    /* Report that the user typed something, no trigger matched, then they
                       clicked a link */

                    analyticsTrack_search_multiple_results_then_click(
                      {
                        term: searchTerm,
                        persona: linkAudience.persona,
                        careCategory: linkAudience.careCategory,
                        patientGroup: linkAudience.patientGroup,
                        ranking: index,
                        title: v.title,
                        slug: v.document?.slug || '',
                        searchId: searchId,
                        type: 'matches',
                        search_source: searchSourceType,
                      },
                      pearlUserInfo
                    );
                  }}
                  className={styles.titleLink}
                >
                  {canViewSearchPreview ? (
                    <span
                      className={styles.docVersionTitle}
                      dangerouslySetInnerHTML={{ __html: getDocumentVersionTitle(v) }}
                    ></span>
                  ) : (
                    <>{v.title}</>
                  )}
                </Link>
                {v.state !== 'PUBLISHED' && (
                  <Badge id="docversion-state" className={styles.stateBadge}>
                    {documentVersionStateToStr(v?.state)}
                  </Badge>
                )}
                {canViewSearchPreview && documentVersionMetaDataHighlight && doVersionPreviewsExist && (
                  <DocumentSearchPreview documentVersionMetaDataHighlight={documentVersionMetaDataHighlight} />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

type InnerMessageExtraMultipleMatchesProps = NonNullable<MessageType['extra']> & { type: 'MULTIPLE_MATCHES' };

/** This is used for in InnerMessageExtra when multiple matches are found
 */
export function MultipleMatches(args: InnerMessageExtraMultipleMatchesProps): JSX.Element | null {
  const audienceFilter = useAudienceFilter();
  const [moreResults, setMoreResults] = useState<undefined | null | SearchDocumentVersionType>(undefined);

  const searchCategories = useContext(SearchCategoriesContext);
  const { searchTerm, versions, publishedOnly, howActivated } = args;
  const showMoreButton = !moreResults && versions.length < args.totalVersions;

  const isCreatorMode = useAppSelector(selectCreatorMode);
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const searchSourceType = useAppSelector(selectSearchSourceType);
  const canViewSearchPreview = useAppSelector(selectCanViewSearchPreview);

  const filterDocumentsBasedOnFlagAndTriage = (
    doc: DocumentVersionType | DocumentVersionSearchType,
    searchType: DocVersionType
  ) => {
    // apply new triageBoost filter if only algolia search is enabled and the mode is consumer
    if (!isCreatorMode) {
      return searchType === 'TRIAGE' ? doc.metadata?.triageBoost === true : doc.metadata?.triageBoost === false;
    } else {
      return searchType === 'TRIAGE' ? doc.document?.docType === 'TRIAGE' : doc.document?.docType !== 'TRIAGE';
    }
  };

  const triages = (moreResults || versions).filter((v) => filterDocumentsBasedOnFlagAndTriage(v, 'TRIAGE'));
  const documents = (moreResults || versions).filter((v) => filterDocumentsBasedOnFlagAndTriage(v, 'DOCUMENT'));

  const handleShowMoreButtonClick = async () => {
    if (moreResults !== undefined) return;
    setMoreResults(null); // in progress...
    try {
      let { versions: versions_ } = await fetchDocumentVersionsSearch(
        searchTerm,
        undefined,
        audienceFilter,
        publishedOnly,
        searchCategories,
        false,
        args.totalVersions
      );
      // apply sorting only if algolia search is disabled and the mode is consumer
      // if (!isCreatorMode) {
      //   versions_.sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));
      // }
      setMoreResults(versions_);
    } catch (e) {
      snackNotifications.error('Error loading more results');
      setMoreResults(undefined);
    }
  };
  useEffect(() => {
    analyticsTrack(
      'search_multiple_results',
      {
        term: searchTerm,
        persona: audienceFilter?.persona || 'Provider',
        careCategory: audienceFilter?.careCategory || 'Outpatient',
        patientGroup: audienceFilter?.patientGroup || 'Adult and Peds',
        howActivated,
        numResults: versions.length,
        searchId: args.searchId,
        session_id: pearlUserInfo?.sessionId,
        search_source: searchSourceType,
      },
      pearlUserInfo
    );
  }, [searchTerm, audienceFilter, howActivated, versions, args.searchId, pearlUserInfo, searchSourceType]);

  return (
    <div className={styles.multipleMatchesAndMedicationContainer}>
      <div className={styles.multipleMatches}>
        {canViewSearchPreview && (
          <span className={styles.synonymMessage}>Results may include synonyms for {<em>{searchTerm}</em>} </span>
        )}
        {triages.length === 0 || !audienceFilter ? undefined : (
          <DocumentVersionList
            audienceFilter={audienceFilter}
            searchTerm={searchTerm}
            documentVersions={triages}
            publishedOnly={publishedOnly}
            showingMoreResults={!!moreResults}
            showNumMatches={!!moreResults || triages.length < args.totalVersions}
            areTriages={true}
            sayAlso={false}
            searchId={args.searchId}
            showAudienceLabels={true && isCreatorMode}
          />
        )}
        {documents.length === 0 || !audienceFilter ? undefined : (
          <DocumentVersionList
            audienceFilter={audienceFilter}
            searchTerm={searchTerm}
            documentVersions={documents}
            publishedOnly={publishedOnly}
            showingMoreResults={!!moreResults}
            showNumMatches={!!moreResults || versions.length === args.totalVersions}
            areTriages={false}
            sayAlso={triages.length > 0}
            searchId={args.searchId}
            triagesLength={triages.length}
            showAudienceLabels={triages.length === 0 && isCreatorMode}
          />
        )}
        {showMoreButton && (
          <div
            className={`${styles.morebutton} ${moreResults === null && styles.loading}`}
            onClick={handleShowMoreButtonClick}
          >
            Show more...
          </div>
        )}
      </div>
      <Medication />
    </div>
  );
}
export function DocumentSearchPreview({
  documentVersionMetaDataHighlight: { doc_categories, tags, content },
}: {
  documentVersionMetaDataHighlight: DocumentMetadataHighlightType;
}) {
  return (
    <div className={styles.summaryContainer}>
      {doc_categories?.has_highlight && doc_categories.value ? (
        <div className={styles.tagsAndSpecialities}>
          {doc_categories.value.map((category, index) => (
            <span className={styles.specialty} key={index} dangerouslySetInnerHTML={{ __html: category }}></span>
          ))}
        </div>
      ) : tags?.has_highlight && tags.value ? (
        <div className={styles.tagsAndSpecialities}>
          {tags.value.map((tag, index) => (
            <span className={styles.tag} key={index} dangerouslySetInnerHTML={{ __html: tag }}></span>
          ))}
        </div>
      ) : null}
      {content?.value && (
        <span className={styles.summary} dangerouslySetInnerHTML={{ __html: fixUpSummary(content.value) }}></span>
      )}
    </div>
  );
}
